import React, {
  useMemo,
  useEffect,
  useReducer
} from 'react';
import { node } from 'prop-types';
import { useAppConfigContext } from '../../hooks/useAppConfigContext';
import { SearchContext } from '../../contexts/SearchContext';
import { SearchReducer } from '../../reducers/SearchReducer';
import { isEmpty } from '../../utils/utilities';
import SearchActionTypes from '../../reducers/SearchReducer/SearchActionTypes';
import { getSearchParams, getSearchTermFromUrl } from '../../utils/searchQuery';
import { useFacetContext } from '../../hooks/useFacetContext';
import { formatFacetValues } from '../../utils/facets';
import { type IChildrenProps } from '../../types/commonTypes';
import { addToDataLayer } from '../../utils/dataLayer';

export const SearchProvider: React.FC<IChildrenProps> = ({ children }) => {
  // Get the site config from one of the expected locations
  // and configure the product search. For Drupal websites, this
  // will be the Drupal settings.
  const initialSearchParams = getSearchParams();

  // Get the app config state.
  const {
    appConfig
  } = useAppConfigContext();

  // Get the facet state.
  const {
    facetValues
  } = useFacetContext();

  // Store the current product search parameters in state.
  const [searchParams, setSearchParams] = useReducer(
    SearchReducer,
    initialSearchParams
  );

  // Set facets from facet context.
  useEffect(() => {
    if (!isEmpty(facetValues)) {
      setSearchParams({
        type: SearchActionTypes.SET_FACETS,
        payload: formatFacetValues(facetValues)
      });
      // Send to data Layer.
      addToDataLayer({ filters: facetValues });
    }
  }, [facetValues]);

  // Set search term from url if present else default.
  useEffect(() => {
    let searchTerm = searchParams.q;

    if (!isEmpty(document.location.search)) {
      const queryTerm = getSearchTermFromUrl(document.location.search);
      searchTerm = !isEmpty(queryTerm) ? queryTerm : searchTerm;
    }

    setSearchParams({
      type: SearchActionTypes.SET_SEARCH_TERM,
      payload: searchTerm
    });

    // Send to data Layer.
    addToDataLayer({ term: searchTerm });
  }, []);

  // You should use useMemo to memoize the values returned to the Context Provider.
  // It reduces context consumers from re-rendering if no changes occur.
  const SearchContextValue = useMemo(() => ({
    searchParams,
    setSearchParams
  }), [searchParams]);

  return (
    <SearchContext.Provider value={SearchContextValue}>{children}</SearchContext.Provider>
  );
};

SearchProvider.propTypes = {
  children: node.isRequired
};

SearchProvider.defaultProps = {};

export default SearchProvider;
